import { type BatchCall } from '@aquariusdefi/sdk';
import { xdr } from '@stellar/stellar-sdk';

import PromisedTimeout from 'helpers/promised-timeout';

import { getAquariusClient } from './aquariusSdk.lazy';

export type SimulateBatchOptions = {
    // Group adjacent results (e.g. estimate + fees pairs) when making chunks.
    // The SDK can split a chunk further if the RPC resource budget is exceeded.
    unitSize?: number;
    // How many calls to pack into a single simulation. Heavy calls need a
    // small value; cheap reads (token metadata) can go much higher. Budget
    // overflows are bisected automatically, so this is a starting point,
    // not a hard limit.
    maxCallsPerBatch?: number;
};

export const buildBatchCall = (
    contractId: string,
    method: string,
    args: xdr.ScVal[],
): BatchCall => ({ contractId, fn: method, args });

// Global cap on concurrent SDK batch requests across ALL groups (rewards,
// positions, balances, metadata, ...). Without it, several groups starting at
// once (e.g. right after login) stack up to dozens of parallel POSTs and can
// crowd out other network activity.
const GLOBAL_CONCURRENCY = 4;
let runningSimulations = 0;
const pendingSimulations: (() => void)[] = [];

const acquireSimulationSlot = (): Promise<void> =>
    new Promise(resolve => {
        if (runningSimulations < GLOBAL_CONCURRENCY) {
            runningSimulations += 1;
            resolve();
            return;
        }
        pendingSimulations.push(() => {
            runningSimulations += 1;
            resolve();
        });
    });

const releaseSimulationSlot = (): void => {
    runningSimulations -= 1;
    const next = pendingSimulations.shift();
    if (next) {
        next();
    }
};

const simulateBatchUnits = async (
    accountId: string,
    units: BatchCall[][],
    attempt = 0,
): Promise<xdr.ScVal[]> => {
    const { AquariusError } = await import('@aquariusdefi/sdk');
    let failure: unknown;
    await acquireSimulationSlot();
    try {
        return await (await getAquariusClient(accountId)).batch.read(units.flat());
    } catch (error) {
        failure = error;
    } finally {
        releaseSimulationSlot();
    }
    // The SDK handles resource-budget splitting. Retry transport errors only.
    if (attempt < 2 && !(failure instanceof AquariusError)) {
        await PromisedTimeout(500 * (attempt + 1));
        return simulateBatchUnits(accountId, units, attempt + 1);
    }
    throw failure;
};

/**
 * Simulates a list of read-only contract calls through the batcher contract,
 * chunking them to stay within per-simulation resource limits.
 * Returns one retval per call, in the same order.
 */
export const simulateBatch = async (
    accountId: string,
    calls: BatchCall[],
    { unitSize = 1, maxCallsPerBatch = 4 }: SimulateBatchOptions = {},
): Promise<xdr.ScVal[]> => {
    if (!calls.length) {
        return [];
    }

    const units: BatchCall[][] = [];
    for (let i = 0; i < calls.length; i += unitSize) {
        units.push(calls.slice(i, i + unitSize));
    }

    const unitsPerChunk = Math.max(1, Math.floor(maxCallsPerBatch / unitSize));
    const chunks: BatchCall[][][] = [];
    for (let i = 0; i < units.length; i += unitsPerChunk) {
        chunks.push(units.slice(i, i + unitsPerChunk));
    }

    // Limited concurrency: full parallel bursts get rate-limited (503) by
    // public RPC nodes, while strictly sequential execution makes large
    // batches painfully slow.
    const CONCURRENCY = 3;
    const results: xdr.ScVal[][] = new Array(chunks.length);
    let cursor = 0;

    const worker = async () => {
        while (cursor < chunks.length) {
            const index = cursor;
            cursor += 1;
            results[index] = await simulateBatchUnits(accountId, chunks[index]);
        }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, () => worker()));

    return results.flat();
};
